import React, { useState } from "react";
import { Link } from "react-router-dom";
import PageTopSlider from "../components/PageTopSlider";
import ReviewsSlider from "../components/ReviewsSlider";
import ContactForm from "../components/ContactForm";
import SEO from "../components/SEO";

export const FUGT_CITY_DATA = {
  horsens: {
    cityName: "Horsens",
    slug: "horsens",
    seoTitle: "Fugtmåling i Horsens – lokal energikonsulent | ET Danmark",
    metaDesc:
      "Fugtmåling i Horsens og omegn efter vandskade, i kælder, badeværelse eller tag. Professionelt Tramex-udstyr og rapport. Ring 42 49 95 95.",
    h1: "Fugtmåling i Horsens – fra din lokale energikonsulent",
    intro:
      "ET Danmark har base på Bjerrevej i Horsens. Har du haft en vandskade, en fugtig kælder eller mistanke om en utæthed i Horsens, Hatting, Brædstrup, Gedved eller omegnen, kan vi fugtmåle konstruktionen og give dig en klar vurdering af, hvor fugten er.",
    localSectionTitle: "Kort vej, når det haster",
    localSectionText:
      "Efter en vandskade er det vigtigt at vide, hvor langt vandet har bredt sig, før der rives ned eller sættes affugtere op. Fordi vi har base i Horsens, er der kort vej ud til dig. Vi måler gulv, vægge og træværk, og du får en rapport, du kan bruge i dialogen med din håndværker.",
    faqs: [
      {
        q: "Kan I måle, om min kælder er fugtig fra jorden eller af kondens?",
        a: "Vi måler både i væggene og gulvet og i rumluften (luftfugtighed, temperatur og dugpunkt). Sammen giver det et godt billede af, hvor fugten kommer fra.",
      },
      {
        q: "Kan I også lave termografi og trykprøve i Horsens?",
        a: "Ja. Se vores side om trykprøvning i Horsens (/trykproevning-i-horsens). Vi kan lave flere målinger ved samme besøg.",
      },
      {
        q: "Skal I bore i mine vægge?",
        a: "Som udgangspunkt nej. Vi scanner gennem overfladen. I træ og gips bruger vi en nåleprobe, der kun efterlader to små huller.",
      },
    ],
  },
  aarhus: {
    cityName: "Aarhus",
    slug: "aarhus",
    seoTitle: "Fugtmåling i Aarhus – nybyggeri, tag og beton | ET Danmark",
    metaDesc:
      "Fugtmåling i Aarhus af betongulve, flade tage, vægge og træ. Til bygherrer og entreprenører. Professionelt Tramex-udstyr. Få et tilbud i dag.",
    h1: "Fugtmåling i Aarhus – til nybyggeri, tage og erhverv",
    intro:
      "I og omkring Aarhus bygges der meget nyt – fra enfamiliehuse til etageboliger og erhverv. Fugt, der lukkes inde i en ny konstruktion, kan blive en dyr fejl. Vi fugtmåler betongulve, flade tage, vægge og træ, så du ved, hvor der er fugt, før næste fag går i gang.",
    localSectionTitle: "Til entreprenører og byggeledere",
    localSectionText:
      "Skal der lægges gulv på et nyt betondæk, kan vi screene hele fladen ikke-destruktivt og finde de fugtigste områder. På flade tage scanner vi tagfladen systematisk og finder de områder, hvor der står vand under tagpappen. Vi laver gerne trykprøven ved samme besøg.",
    faqs: [
      {
        q: "Kan I dokumentere RF i betonen inden gulvlægning?",
        a: "Den ikke-destruktive måling viser, hvor betonen er fugtigst i de øverste ca. 2 cm. Kræver gulvleverandøren en dokumenteret RF-værdi, skal der laves en RF-måling i borehuller. Vi rådgiver om, hvad der skal til.",
      },
      {
        q: "Kan I scanne store tagflader på erhvervsbyggeri?",
        a: "Ja. Vi har en tagskanner på hjul til store, flade tage. Tagfladen skal være tør på overfladen, og metoden virker ikke gennem metal eller på visse gummimembraner.",
      },
      {
        q: "Laver I også trykprøve og energimærke i Aarhus?",
        a: "Ja. Se vores side om trykprøvning i Aarhus (/trykproevning-i-aarhus).",
      },
    ],
  },
  odense: {
    cityName: "Odense",
    slug: "odense",
    seoTitle: "Fugtmåling i Odense og på Fyn | ET Danmark",
    metaDesc:
      "Fugtmåling i Odense og på hele Fyn – før boligkøb, efter vandskade eller ved utæt tag. Professionelt Tramex-udstyr og rapport. Ring 42 49 95 95.",
    h1: "Fugtmåling i Odense og på hele Fyn",
    intro:
      "Skal du købe hus i Odense eller et andet sted på Fyn, eller har du mistanke om fugt i din egen bolig? Vi fugtmåler kælder, badeværelse, vægge, gulve og tag og giver dig en klar vurdering, før du træffer en beslutning.",
    localSectionTitle: "Til dig, der skal købe bolig",
    localSectionText:
      "Er der en kælder, et badeværelse eller et fladt tag, du er i tvivl om, kan vi måle netop det område, før du skriver under. Målingen er et supplement til tilstandsrapporten og giver dig et konkret billede af, om der er fugt, og hvor den sidder. Skal huset også have et energimærke, kan vi lave begge dele ved samme besøg.",
    faqs: [
      {
        q: "Kan I nå at måle, før jeg skriver under?",
        a: "Ring på 42 49 95 95, så finder vi hurtigt en tid.",
      },
      {
        q: "Kan I måle fugt i et fladt tag med tagpap?",
        a: "Ja. Vi scanner tagfladen uden at skære i tagpappen og finder de områder, hvor der er fugt i opbygningen. Tagfladen skal være tør på overfladen.",
      },
      {
        q: "Dækker I hele Fyn?",
        a: "Ja, hele Fyn. Ring for en konkret aftale.",
      },
    ],
  },
};

export default function CityFugtmaaling({ cityKey }) {
  const [openFaq, setOpenFaq] = useState(null);
  const data = FUGT_CITY_DATA[cityKey] || FUGT_CITY_DATA.horsens;

  const cityServiceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `https://www.etdanmark.com/fugtmaaling-i-${data.slug}#service`,
    name: `Fugtmåling i ${data.cityName}`,
    serviceType: "Fugtmåling med Tramex",
    description: data.metaDesc,
    url: `https://www.etdanmark.com/fugtmaaling-i-${data.slug}`,
    offers: {
      "@type": "Offer",
      priceCurrency: "DKK",
      priceSpecification: {
        "@type": "PriceSpecification",
        minPrice: 5000,
        priceCurrency: "DKK",
        valueAddedTaxIncluded: true,
      },
      description:
        "Fugtmåling i enfamiliehus fra 5.000 kr. inkl. moms. Flerfamilie og erhverv fra 5.000 kr. + moms. Rapport inden for 2–5 hverdage.",
    },
    areaServed: {
      "@type": "City",
      name: data.cityName,
    },
    provider: {
      "@type": "LocalBusiness",
      "@id": "https://www.etdanmark.com/#organization",
      name: "ET Danmark ApS",
      url: "https://www.etdanmark.com/",
      telephone: "+4542499595",
      email: "info@etdanmark.com",
    },
  };

  const cityFaqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `https://www.etdanmark.com/fugtmaaling-i-${data.slug}#faq`,
    mainEntity: data.faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.a,
      },
    })),
  };

  return (
    <div className="bg-[#FAF9F6] text-stone-800">
      <SEO
        title={data.seoTitle}
        description={data.metaDesc}
        canonical={`https://www.etdanmark.com/fugtmaaling-i-${data.slug}`}
        ogImage="https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07589.webp"
        schemas={[cityServiceSchema, cityFaqSchema]}
      />

      {/* 1. TOP HEADER */}
      <PageTopSlider
        title={data.h1}
        subtitle={data.intro}
        badge={`Lokal ekspertise i ${data.cityName}`}
        breadcrumbs={[
          { label: "Forside", to: "/" },
          { label: "Fugtmåling", to: "/fugtmaaling" },
          { label: `${data.cityName}` },
        ]}
        priceBadge="Fra 5.000 kr. inkl. moms"
        actions={
          <>
            <a
              href="#kontakt"
              className="px-6 py-3 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow-md transition-all flex items-center gap-2"
            >
              <i className="fa-solid fa-droplet text-xs" aria-hidden="true"></i>
              <span>Få et uforpligtende tilbud på fugtmåling</span>
            </a>
            <a
              href="tel:+4542499595"
              className="px-6 py-3 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 text-white font-bold text-sm backdrop-blur-xs transition-all flex items-center gap-2"
            >
              <i className="fa-solid fa-phone text-emerald-300 text-xs" aria-hidden="true"></i>
              <span>Ring til Lars: 42 49 95 95</span>
            </a>
          </>
        }
        image={{
          url: "https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07546.jpg",
          alt: `Fugtmåling i ${data.cityName} med Tramex udstyr`,
        }}
      />

      {/* 2. UNIK LOKAL SEKTION */}
      <section className="py-16 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-[#165B4C] text-xs font-bold uppercase tracking-wider">
                <i className="fa-solid fa-location-dot" aria-hidden="true"></i>
                <span>Lokal rådgivning i {data.cityName}</span>
              </div>
              <h2 className="text-3xl font-extrabold text-stone-900 tracking-tight">
                {data.localSectionTitle}
              </h2>
              <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
                {data.localSectionText}
              </p>
              <div className="pt-2 flex flex-wrap gap-4">
                <a
                  href="#kontakt"
                  className="px-6 py-2.5 rounded-full bg-[#165B4C] hover:bg-[#11463A] text-white font-bold text-sm shadow-sm transition-all"
                >
                  Indhent tilbud i {data.cityName}
                </a>
                <a
                  href="tel:+4542499595"
                  className="px-6 py-2.5 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-800 font-semibold text-sm transition-all border border-stone-200"
                >
                  Ring: 42 49 95 95
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="p-7 rounded-3xl bg-[#FAF9F6] border border-stone-200/80 shadow-md space-y-4">
                <h3 className="text-lg font-bold text-stone-900 flex items-center gap-2">
                  <i className="fa-solid fa-shield-halved text-[#165B4C]" aria-hidden="true"></i>
                  <span>Hurtig hjælp i {data.cityName}</span>
                </h3>
                <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                  Uanset om du er privat boligejer eller byggeleder, tilbyder vi hurtig udrykning og grundig måling med professionelt Tramex-udstyr.
                </p>
                <div className="pt-2 border-t border-stone-200/80 text-xs text-stone-500 flex justify-between">
                  <span>Rapport inden for 2–5 hverdage</span>
                  <span className="font-bold text-[#165B4C]">Fast pris</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. FÆLLES BLOK: SÅDAN MÅLER VI */}
      <section className="py-16 bg-stone-50 border-b border-stone-200/70">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
              Sådan måler vi
            </h2>
          </div>
          <div className="bg-white p-8 rounded-3xl border border-stone-200 shadow-sm text-sm sm:text-base text-stone-700 leading-relaxed space-y-4">
            <p>
              Vi måler med professionelt Tramex-udstyr. Tage, vægge og betongulve scannes ikke-destruktivt gennem overfladen, og i træ måler vi fugtindholdet med en nåleprobe. Den ikke-destruktive måling viser, om og hvor der er fugt. Er der brug for et dokumenteret tal, fx en RF-måling i borehuller i beton, fortæller vi dig, hvad der skal til.
            </p>
            <div className="pt-2">
              <Link
                to="/fugtmaaling"
                className="text-[#165B4C] hover:text-[#11463A] font-bold inline-flex items-center gap-1.5"
              >
                <span>Læs mere om fugtmåling og metoder</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 4. FÆLLES PRISBLOK */}
      <section className="py-16 bg-white border-b border-stone-200/70">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
              Pris og levering
            </h2>
          </div>
          <div className="p-8 rounded-3xl bg-[#FAF9F6] border border-stone-200/80 shadow-sm space-y-4 text-sm sm:text-base text-stone-700 leading-relaxed">
            <p>
              <strong>Fugtmåling i enfamiliehus:</strong> fra 5.000 kr. inkl. moms (4.000 kr. + moms).<br />
              <strong>Flerfamiliehuse og erhvervs-/industribygninger:</strong> fra 5.000 kr. + moms – endelig pris efter tilbud.<br />
              <strong>Scanning af flade tage:</strong> pris efter tilbud.
            </p>
            <p className="text-xs sm:text-sm text-stone-600">
              Du kender prisen, før vi går i gang. Rapporten sendes inden for 2–5 hverdage efter målingen. Bestiller du fugtmåling sammen med termografi eller trykprøve, kan vi lave det ved samme besøg.
            </p>
          </div>
        </div>
      </section>

      {/* 5. BY-FAQ */}
      <section className="py-16 bg-stone-50 border-b border-stone-200/70">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
              Spørgsmål om fugtmåling i {data.cityName}
            </h2>
          </div>

          <div className="space-y-3">
            {data.faqs.map((faq, idx) => (
              <div
                key={idx}
                className="rounded-2xl border border-stone-200 bg-white overflow-hidden transition-all"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full py-4 px-6 text-left font-bold text-stone-900 flex items-center justify-between gap-4 text-sm sm:text-base hover:text-[#165B4C] transition-colors"
                >
                  <span>{faq.q}</span>
                  <i
                    className={`fa-solid fa-chevron-down text-xs text-stone-500 transition-transform duration-200 shrink-0 ${
                      openFaq === idx ? "rotate-180 text-[#165B4C]" : ""
                    }`}
                    aria-hidden="true"
                  ></i>
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-5 text-stone-600 text-xs sm:text-sm leading-relaxed border-t border-stone-100 pt-3">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. SOCIAL PROOF */}
      <ReviewsSlider />

      {/* 7. KRYDSLINKS TIL ANDRE YDELSER I SAMME BY */}
      <section className="py-12 bg-white border-b border-stone-200/70">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-6">
            <h3 className="text-xl font-bold text-stone-900">
              Flere ydelser i {data.cityName}
            </h3>
            <p className="text-xs sm:text-sm text-stone-500 mt-1">
              Få samlet alle målinger og dokumentation hos én erfaren energikonsulent
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to={`/energimaerkning-i-${data.slug}`}
              className="px-5 py-2.5 rounded-full bg-stone-100 hover:bg-[#165B4C] hover:text-white text-stone-800 text-xs font-semibold transition-colors"
            >
              Energimærkning i {data.cityName} &rarr;
            </Link>
            <Link
              to={`/trykproevning-i-${data.slug}`}
              className="px-5 py-2.5 rounded-full bg-stone-100 hover:bg-[#165B4C] hover:text-white text-stone-800 text-xs font-semibold transition-colors"
            >
              Trykprøvning i {data.cityName} &rarr;
            </Link>
            <Link
              to={`/energikonsulent-i-${data.slug}`}
              className="px-5 py-2.5 rounded-full bg-stone-100 hover:bg-[#165B4C] hover:text-white text-stone-800 text-xs font-semibold transition-colors"
            >
              Energikonsulent i {data.cityName} &rarr;
            </Link>
          </div>
        </div>
      </section>

      {/* 8. KONTAKT FORMULAR */}
      <section className="py-16 sm:py-20 bg-stone-50" id="kontakt">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold text-stone-900 tracking-tight">
              Få et uforpligtende tilbud på fugtmåling i {data.cityName}
            </h2>
            <p className="text-stone-600 text-sm mt-2">
              Udfyld formularen eller ring direkte på 42 49 95 95. Vi rykker hurtigt ud i hele {data.cityName} og omegn!
            </p>
          </div>

          <div className="bg-white p-6 sm:p-10 rounded-3xl border border-stone-200 shadow-xl">
            <ContactForm
              defaultService="Fugtmåling"
              title={`Fugtmåling i ${data.cityName}`}
              subtitle="Fortæl os kort om din opgave – vi vender hurtigt tilbage med en fast pris."
            />
          </div>
        </div>
      </section>
    </div>
  );
}
